import type { CoreBus, IncomingSignal } from "./bus";
import { isCaller } from "../lib/room-helpers";
import { addMissingMimeTypesToFiles } from "./mime";

export type ManagedPeer = {
  peerId: string;
  onServerMessage(message: IncomingSignal): void;
  requestFileTransfer(files: File[]): Promise<void>;
  sendText(text: string): void;
  respondToFileTransferRequest(accepted: boolean): void;
  sendDisplayName(displayName: string): void;
  cancelTransfer(): void;
};

export type PeersManagerDeps = {
  bus: CoreBus;
  selfPeerId: string;
  createPeer: (isCaller: boolean, peerId: string) => ManagedPeer;
  onPeerRemoved?: (peerId: string) => void; // Phase 3 hook closes the RTCPeerConnection
};

export class PeersManager {
  private readonly bus: CoreBus;
  private readonly selfPeerId: string;
  private readonly createPeer: PeersManagerDeps["createPeer"];
  private readonly onPeerRemoved?: (peerId: string) => void;
  private readonly peers = new Map<string, ManagedPeer>();

  private displayName = "";
  private originalDisplayName = "";

  constructor(deps: PeersManagerDeps) {
    this.bus = deps.bus;
    this.selfPeerId = deps.selfPeerId;
    this.createPeer = deps.createPeer;
    this.onPeerRemoved = deps.onPeerRemoved;

    this.bus.on("signal", (m) => this._onSignal(m));
    this.bus.on("peer-joined", (m) => this._onPeerJoined(m.peer.peerId));
    this.bus.on("peer-left", (m) => this.bus.emit("peer-disconnected", m.peerId));
    this.bus.on("peer-connected", (m) => this._notifyPeerDisplayNameChanged(m.peerId));
    this.bus.on("peer-disconnected", (peerId) => this._onPeerDisconnected(peerId));
    this.bus.on("files-selected", (m) => void this._onFilesSelected(m));
    this.bus.on("respond-to-files-transfer-request", (m) => this.peers.get(m.to)?.respondToFileTransferRequest(m.accepted));
    this.bus.on("send-text", (m) => this.peers.get(m.to)?.sendText(m.text));
    this.bus.on("cancel-file-transfer", (m) => this.peers.get(m.to)?.cancelTransfer());
    this.bus.on("display-name", (m) => this._onDisplayName(m.displayName));
    this.bus.on("self-display-name-changed", (name) => this._notifyPeersDisplayNameChanged(name));
    this.bus.on("notify-peer-display-name-changed", (peerId) => this._notifyPeerDisplayNameChanged(peerId));
  }

  private _onSignal(message: IncomingSignal): void {
    if (!this.peers.has(message.from)) this._createPeer(false, message.from);
    this.peers.get(message.from)!.onServerMessage(message);
  }

  private _onPeerJoined(peerId: string): void {
    if (this.peers.has(peerId)) return;
    this._createPeer(isCaller(this.selfPeerId, peerId), peerId);
  }

  private _createPeer(amCaller: boolean, peerId: string): void {
    this.peers.set(peerId, this.createPeer(amCaller, peerId));
  }

  private async _onFilesSelected(message: { to: string; files: File[] }): Promise<void> {
    const files = addMissingMimeTypesToFiles([...message.files]);
    await this.peers.get(message.to)?.requestFileTransfer(files);
  }

  private _onPeerDisconnected(peerId: string): void {
    if (!this.peers.has(peerId)) return;
    this.peers.delete(peerId);
    this.onPeerRemoved?.(peerId);
  }

  private _notifyPeersDisplayNameChanged(newDisplayName: string): void {
    this.displayName = newDisplayName || this.originalDisplayName;
    for (const peerId of this.peers.keys()) this._notifyPeerDisplayNameChanged(peerId);
  }

  private _notifyPeerDisplayNameChanged(peerId: string): void {
    this.peers.get(peerId)?.sendDisplayName(this.displayName);
  }

  private _onDisplayName(displayName: string): void {
    this.originalDisplayName = displayName;
    if (!this.displayName) this.displayName = displayName;
  }
}
